import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { FiFileText, FiCheckCircle, FiClock, FiXCircle, FiGrid, FiPlusCircle, FiPauseCircle, FiUsers, FiBriefcase, FiMessageSquare, FiUserPlus, FiMail } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { usersApi, campaignsApi, adminApi, getImageUrl } from '../../utils/api';
import './DashboardPage.css';

const MotionLink = motion(Link);

const DashboardPage = () => {
  const { user } = useAuth();
  const prevLeadsCount = useRef(null);
  const prevCreatorsCount = useRef(null);

  const isApprovedCreator = user?.role === 'creator' && user?.status === 'approved';
  const isPendingCreator = user?.role === 'creator' && user?.status !== 'approved';

  const { data: statsData } = useQuery(
    ['user-stats'],
    () => usersApi.getStats(),
    { enabled: !!user && !isPendingCreator, refetchInterval: 15000, refetchOnWindowFocus: true }
  );

  const { data: campaignsData } = useQuery(
    ['featured-campaigns'],
    () => campaignsApi.getAll({ featured: true, limit: 4 }),
    { enabled: isApprovedCreator }
  );

  const { data: myCampaignsData } = useQuery(
    ['my-campaigns'],
    () => campaignsApi.getMy({ limit: 4 }),
    { enabled: user?.role === 'brand' }
  );

  const { data: adminStatsData } = useQuery(
    ['admin-stats'],
    () => adminApi.getStats(),
    {
      enabled: user?.role === 'admin',
      refetchInterval: 10000 // Refresh every 10 seconds
    }
  );

  const stats = statsData?.data?.stats;
  const adminStats = adminStatsData?.data?.stats;

  // Real-time notifications for new leads/users
  useEffect(() => {
    if (user?.role === 'admin' && adminStats) {
      const currentLeads = adminStats.users?.brands || 0;
      const currentCreators = adminStats.users?.creators || 0;

      // Check for new leads
      if (prevLeadsCount.current !== null && currentLeads > prevLeadsCount.current) {
        const newCount = currentLeads - prevLeadsCount.current;
        toast.info(
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FiBriefcase size={20} />
            <span>{newCount === 1 ? 'ליד חדש נרשם!' : `${newCount} לידים חדשים נרשמו!`}</span>
          </div>,
          { autoClose: 5000 }
        );
      }

      // Check for new creators
      if (prevCreatorsCount.current !== null && currentCreators > prevCreatorsCount.current) {
        const newCount = currentCreators - prevCreatorsCount.current;
        toast.success(
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FiUserPlus size={20} />
            <span>{newCount === 1 ? 'משפיען חדש נרשם!' : `${newCount} משפיענים חדשים נרשמו!`}</span>
          </div>,
          { autoClose: 5000 }
        );
      }

      prevLeadsCount.current = currentLeads;
      prevCreatorsCount.current = currentCreators;
    }
  }, [adminStats, user?.role]);

  const creatorStats = user?.role === 'creator' ? [
    { label: 'סה"כ הגשות', value: stats?.total || 0, icon: FiFileText, color: 'primary', to: '/dashboard/applications' },
    { label: 'ממתינות', value: stats?.pending || 0, icon: FiClock, color: 'warning', to: '/dashboard/applications' },
    { label: 'אושרו', value: stats?.approved || 0, icon: FiCheckCircle, color: 'success', to: '/dashboard/applications' },
    { label: 'נדחו', value: stats?.rejected || 0, icon: FiXCircle, color: 'error', to: '/dashboard/applications' }
  ] : [];

  const brandStats = user?.role === 'brand' ? [
    { label: 'סה"כ קמפיינים', value: stats?.total || 0, icon: FiFileText, color: 'primary', to: '/dashboard/campaigns' },
    { label: 'פעילים', value: stats?.active || 0, icon: FiCheckCircle, color: 'success', to: '/dashboard/campaigns' },
    { label: 'טיוטות', value: stats?.draft || 0, icon: FiClock, color: 'warning', to: '/dashboard/campaigns' },
    { label: 'סגורים', value: stats?.closed || 0, icon: FiPauseCircle, color: 'error', to: '/dashboard/campaigns' }
  ] : [];

  const adminDashboardStats = user?.role === 'admin' ? [
    { label: 'משפיענים', value: adminStats?.users?.creators || 0, icon: FiUsers, color: 'primary', to: '/dashboard/admin/creators/approved' },
    { label: 'מותגים', value: adminStats?.users?.brands || 0, icon: FiBriefcase, color: 'success', to: '/dashboard/admin/brands' },
    { label: 'קמפיינים', value: adminStats?.campaigns?.total || 0, icon: FiGrid, color: 'warning', to: '/dashboard/admin/campaigns' },
    { label: 'הגשות ממתינות', value: adminStats?.applications?.pending || 0, icon: FiClock, color: 'error', to: '/dashboard/admin/applications/pending' }
  ] : [];

  return (
    <div className="dashboard-page">
      {/* Welcome Section */}
      <section className="welcome-section">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1>שלום, {user?.displayName || user?.fullName?.split(' ')[0] || 'אורח'}!</h1>
          <p>
            {user?.role === 'admin'
              ? 'נהל את הפלטפורמה ואשר הגשות'
              : isPendingCreator
              ? 'הפרופיל שלך בבדיקה - נעדכן אותך בקרוב!'
              : user?.role === 'creator'
              ? 'גלה קמפיינים חדשים ועקוב אחרי ההגשות שלך'
              : 'נהל את הקמפיינים שלך וצפה בהגשות'}
          </p>
        </motion.div>
      </section>

      {/* Pending Approval Banner - Creator */}
      {isPendingCreator && (
        <motion.section
          className="pending-approval-banner"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="pending-approval-content">
            <div className="pending-icon">
              <FiClock />
            </div>
            <div className="pending-text">
              <h3>הפרופיל שלך ממתין לאישור</h3>
              <p>הצוות שלנו בודק את הפרופיל שלך. ברגע שהפרופיל יאושר, תקבל/י מייל עם קישור להתחברות ותוכל/י לגשת לקמפיינים ולהגיש מועמדויות.</p>
            </div>
            <div className="pending-info">
              <div className="info-item">
                <FiClock className="info-icon" />
                <span>בדיקת הפרופיל בתהליך</span>
              </div>
              <div className="info-item">
                <FiMail className="info-icon" />
                <span>נעדכן אותך במייל כשהפרופיל יאושר</span>
              </div>
            </div>
          </div>
        </motion.section>
      )}

      {/* Stats Section - Creator */}
      {isApprovedCreator && (
        <section className="stats-section">
          <div className="stats-grid">
            {creatorStats.map((stat, index) => (
              <MotionLink
                key={stat.label}
                to={stat.to}
                className={`stat-card stat-${stat.color}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="stat-icon">
                  <stat.icon />
                </div>
                <div className="stat-info">
                  <span className="stat-value">{stat.value}</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              </MotionLink>
            ))}
          </div>
        </section>
      )}

      {/* Stats Section - Brand */}
      {user?.role === 'brand' && (
        <section className="stats-section">
          <div className="stats-grid">
            {brandStats.map((stat, index) => (
              <MotionLink
                key={stat.label}
                to={stat.to}
                className={`stat-card stat-${stat.color}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="stat-icon">
                  <stat.icon />
                </div>
                <div className="stat-info">
                  <span className="stat-value">{stat.value}</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              </MotionLink>
            ))}
          </div>
        </section>
      )}

      {/* Stats Section - Admin */}
      {user?.role === 'admin' && (
        <section className="stats-section">
          <div className="stats-grid">
            {adminDashboardStats.map((stat, index) => (
              <MotionLink
                key={stat.label}
                to={stat.to}
                className={`stat-card stat-${stat.color}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="stat-icon">
                  <stat.icon />
                </div>
                <div className="stat-info">
                  <span className="stat-value">{stat.value}</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              </MotionLink>
            ))}
          </div>
        </section>
      )}

      {/* Quick Actions - Admin */}
      {user?.role === 'admin' && (
        <section className="quick-actions">
          <div className="actions-grid">
            <Link to="/dashboard/admin/creators" className="action-card">
              <FiUsers className="action-icon" />
              <span>פרופילים</span>
            </Link>
            <Link to="/dashboard/admin/brands" className="action-card">
              <FiBriefcase className="action-icon" />
              <span>לידים</span>
            </Link>
            <Link to="/dashboard/admin/applications" className="action-card primary">
              <FiFileText className="action-icon" />
              <span>מועמדות משפיענים</span>
            </Link>
            <Link to="/dashboard/admin/messages" className="action-card">
              <FiMessageSquare className="action-icon" />
              <span>שלח הודעה</span>
            </Link>
          </div>
        </section>
      )}

      {/* Quick Actions - Brand */}
      {user?.role === 'brand' && (
        <section className="quick-actions">
          <div className="actions-grid">
            <Link to="/dashboard/campaigns/create" className="action-card primary">
              <FiPlusCircle className="action-icon" />
              <span>צור קמפיין חדש</span>
            </Link>
            <Link to="/dashboard/campaigns" className="action-card">
              <FiGrid className="action-icon" />
              <span>הקמפיינים שלי</span>
            </Link>
          </div>
        </section>
      )}

      {/* Featured Campaigns - Creator (approved only) */}
      {isApprovedCreator && campaignsData?.data?.campaigns?.length > 0 && (
        <section className="campaigns-section">
          <div className="section-header">
            <h2>קמפיינים מומלצים</h2>
            <Link to="/campaigns" className="view-all-link">צפה בכל הקמפיינים</Link>
          </div>
          <div className="campaigns-grid">
            {campaignsData.data.campaigns.map((campaign) => (
              <Link
                key={campaign._id}
                to={`/campaigns/${campaign._id}`}
                className="campaign-card-mini"
              >
                {campaign.coverImage ? (
                  <img src={getImageUrl(campaign.coverImage)} alt={campaign.title} className="campaign-image" />
                ) : (
                  <div className="campaign-placeholder">
                    {campaign.brandLogo && <img src={getImageUrl(campaign.brandLogo)} alt={campaign.brandName} />}
                  </div>
                )}
                <div className="campaign-info">
                  <span className="campaign-brand">{campaign.brandName}</span>
                  <h3>{campaign.title}</h3>
                  <span className={`badge ${campaign.isOpenForApplications ? 'badge-success' : 'badge-error'}`}>
                    {campaign.isOpenForApplications ? 'פתוח להרשמה' : 'סגור'}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* My Campaigns - Brand */}
      {user?.role === 'brand' && myCampaignsData?.data?.campaigns?.length > 0 && (
        <section className="campaigns-section">
          <div className="section-header">
            <h2>הקמפיינים שלי</h2>
            <Link to="/dashboard/campaigns" className="view-all-link">צפה בכל הקמפיינים</Link>
          </div>
          <div className="campaigns-grid">
            {myCampaignsData.data.campaigns.map((campaign) => (
              <Link
                key={campaign._id}
                to={`/dashboard/campaigns/${campaign._id}/applications`}
                className="campaign-card-mini"
              >
                {campaign.coverImage ? (
                  <img src={getImageUrl(campaign.coverImage)} alt={campaign.title} className="campaign-image" />
                ) : (
                  <div className="campaign-placeholder" />
                )}
                <div className="campaign-info">
                  <h3>{campaign.title}</h3>
                  <div className="campaign-stats">
                    <span>{campaign.applicationsCount || 0} הגשות</span>
                    <span className={`badge badge-${campaign.status === 'active' ? 'success' : 'warning'}`}>
                      {campaign.status === 'active' ? 'פעיל' : campaign.status === 'draft' ? 'טיוטה' : 'סגור'}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Empty State */}
      {isApprovedCreator && (!campaignsData?.data?.campaigns || campaignsData.data.campaigns.length === 0) && (
        <section className="empty-state">
          <div className="empty-content">
            <FiGrid className="empty-icon" />
            <h3>אין קמפיינים להצגה</h3>
            <p>בקרוב יופיעו כאן קמפיינים מומלצים עבורך</p>
            <Link to="/campaigns" className="btn btn-primary">
              גלה קמפיינים
            </Link>
          </div>
        </section>
      )}

      {user?.role === 'brand' && (!myCampaignsData?.data?.campaigns || myCampaignsData.data.campaigns.length === 0) && (
        <section className="empty-state">
          <div className="empty-content">
            <FiPlusCircle className="empty-icon" />
            <h3>עדיין אין לך קמפיינים</h3>
            <p>צור את הקמפיין הראשון שלך והתחל לעבוד עם יוצרי תוכן</p>
            <Link to="/dashboard/campaigns/create" className="btn btn-primary">
              צור קמפיין
            </Link>
          </div>
        </section>
      )}
    </div>
  );
};

export default DashboardPage;
